
/*
 * The analytics widgets (`AreaChartWidget` … `ActivityFeedWidget`) are
 * described in components/widgetPage/widgets/analytics/README.md: measures,
 * expressions, periods, and each widget's own keys. Their measures read the
 * live aggregates an entity declares in server/src/model/entities.js
 * (`supabaseAggregations`), by entity and aggregate name.
 */
export const widgetData =
{
    // Where the result of a period comes from: what was invoiced, what was
    // credited back, what was spent with the largest suppliers. The end bar is
    // computed, so the bridge always adds up.
    "accounting.analytics.result.bridge.widget": {
        widgetType: 'WaterfallWidget',
        widgetData: {
            period: 'last3Months',
            compare: 'previous',
            measures: {
                invoiced: {
                    format: 'currency',
                    sources: [
                        { id: 'invoice', entity: 'invoice', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                    ]
                },
                credited: {
                    format: 'currency',
                    sources: [
                        { id: 'creditNote', entity: 'creditNote', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                    ]
                },
                spend: {
                    format: 'currency',
                    group: 'supplierid',
                    name: 'suppliername',
                    sources: [
                        { id: 'expense', entity: 'expense', aggregation: 'bySupplier', params: { state: 'final' }, value: 'net' },
                    ]
                },
                sales: {
                    format: 'currency',
                    sources: [
                        { id: 'invoice',    entity: 'invoice',    aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                        { id: 'creditNote', entity: 'creditNote', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net', sign: -1 },
                    ]
                },
                result: {
                    format: 'currency',
                    sources: [
                        { id: 'invoice',    entity: 'invoice',    aggregation: 'byPeriod',   params: { state: 'final' }, value: 'net' },
                        { id: 'creditNote', entity: 'creditNote', aggregation: 'byPeriod',   params: { state: 'final' }, value: 'net', sign: -1 },
                        { id: 'expense',    entity: 'expense',    aggregation: 'bySupplier', params: { state: 'final' }, value: 'net', sign: -1 },
                    ]
                },
            },
            waterfall: {
                format: 'currency',
                start: { label: 'accounting.analytics.bridge.invoiced', value: 'invoiced' },
                steps: [
                    { id: 'credited', label: 'accounting.analytics.bridge.credited', value: 'credited', sign: -1 },
                    { id: 'spend',    group: 'spend', limit: 4, sign: -1, otherLabel: 'accounting.analytics.bridge.otherSpend' },
                ],
                end: { label: 'accounting.analytics.bridge.result' },
            },
            kpis: [
                { id: 'result', label: 'accounting.analytics.kpi.result', value: 'result' },
                { id: 'margin', label: 'accounting.analytics.kpi.margin', value: { percentOf: ['result', 'sales'] } },
            ]
        },
        widgetLabels: {
            title: 'accounting.analytics.bridge.title',
            subtitle: 'accounting.analytics.bridge.subtitle',
        }
    },
    // Net sales against booked expenses, month by month. Two independent
    // series on one axis — overlapping, never stacked.
    "accounting.analytics.sales.expenses.widget": {
        widgetType: 'DoubleAreaChartWidget',
        widgetData: {
            period: 'last6Months',
            compare: 'previous',
            measures: {
                sales: {
                    format: 'currency',
                    sources: [
                        { id: 'invoice',    entity: 'invoice',    aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                        { id: 'creditNote', entity: 'creditNote', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net', sign: -1 },
                    ]
                },
                expenses: {
                    format: 'currency',
                    sources: [
                        { id: 'expense', entity: 'expense', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                    ]
                },
            },
            series: [
                { id: 'sales',    label: 'accounting.analytics.series.sales',    value: 'sales',    color: 'primary' },
                { id: 'expenses', label: 'accounting.analytics.series.expenses', value: 'expenses', color: 'secondary' },
            ],
            markers: true,
            kpis: [
                { id: 'sales',    label: 'accounting.analytics.series.sales',    value: 'sales' },
                { id: 'expenses', label: 'accounting.analytics.series.expenses', value: 'expenses', favorable: 'down' },
                { id: 'result',   label: 'accounting.analytics.kpi.result',      value: { subtract: ['sales', 'expenses'] }, format: 'currency' },
            ]
        },
        widgetLabels: {
            title: 'accounting.analytics.salesExpenses.title',
        }
    },
    "contact.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'contact',
            path: '/sales/contact',
            columns: [
                { field: 'name',  label: 'contact.list.table.name' },
                { field: 'email', label: 'contact.list.table.email' },
            ],
        },
        widgetLabels: {
            title: 'contact.list.widget.title',
            emptyDescription: 'contact.list.widget.empty.description',
            emptyAction: 'contact.list.widget.empty.action',
        }
    },
    // The customers who bought the most in the period, net of what was
    // credited back to them, with how often they bought and their share.
    "crm.analytics.top.customers.widget": {
        widgetType: 'DataTableWidget',
        widgetData: {
            period: 'last12Months',
            compare: 'previous',
            measures: {
                sales: {
                    format: 'currency',
                    group: 'customerid',
                    name: 'customername',
                    sources: [
                        { id: 'invoice',    entity: 'invoice',    aggregation: 'byCustomer', params: { state: 'final' }, value: 'net' },
                        { id: 'creditNote', entity: 'creditNote', aggregation: 'byCustomer', params: { state: 'final' }, value: 'net', sign: -1 },
                    ]
                },
                invoices: {
                    format: 'integer',
                    group: 'customerid',
                    name: 'customername',
                    sources: [
                        { id: 'invoice', entity: 'invoice', aggregation: 'byCustomer', params: { state: 'final' }, value: 'count' },
                    ]
                },
            },
            table: {
                groups: { value: 'sales', limit: 8 },
                rowPath: '/crm/contact',
                sort: { column: 'sales', direction: 'desc' },
                columns: [
                    { id: 'name',     label: 'crm.analytics.table.customer', kind: 'name' },
                    { id: 'sales',    label: 'crm.analytics.table.sales',    value: 'sales' },
                    { id: 'invoices', label: 'crm.analytics.table.invoices', value: 'invoices', hideBelow: 340 },
                    { id: 'average',  label: 'crm.analytics.table.average',  value: { divide: ['sales', 'invoices'] }, format: 'currency', hideBelow: 460 },
                    { id: 'share',    label: 'crm.analytics.table.share',    value: { shareOf: 'sales' }, hideBelow: 400 },
                ],
            },
            kpis: [
                { id: 'sales',    label: 'crm.analytics.kpi.sales',    value: 'sales' },
                { id: 'invoices', label: 'crm.analytics.kpi.invoices', value: 'invoices' },
            ]
        },
        widgetLabels: {
            title: 'crm.analytics.topCustomers.title',
        }
    },
    "crm.create.product.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'product',
            path: '/crm/product',
            columns: [
                { field: 'name',        label: 'product.list.table.name' },
                { field: 'description', label: 'product.list.table.description' },
                { field: 'netSalesPrice', label: 'product.list.table.price', format: 'amount' },
            ],
        },
        widgetLabels: {
            title: 'product.list.widget.title',
            emptyDescription: 'product.list.widget.empty.description',
            emptyAction: 'product.list.widget.empty.action',
        }
    },
    "crm.text.widget": {
        widgetType: 'TextWidget',
        widgetData: {
        },
        widgetLabels: {
            title: 'crm.text.widget.title',
            text: 'crm.text.widget.title'
        }
    },
    "document.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'document',
            path: '/documents/document',
            columns: [
                { field: 'name',         label: 'documents.document.list.table.name' },
                { field: 'fileTypeName', label: 'documents.document.list.table.fileType' },
                { field: 'folderPath',   label: 'documents.document.list.table.folderPath' }
            ]
        },
        widgetLabels: {
            title: 'documents.document.list.widget.title',
            emptyDescription: 'documents.document.list.widget.empty.description',
            emptyAction: 'documents.document.list.widget.empty.action'
        }
    },
    "go.crm.widget": {
        widgetType: 'TextWidget',
        widgetData: {
            path: '/crm',
        },
        widgetLabels: {
            title: 'go.crm.widget.title',
            text: 'go.crm.widget.text',
            button: 'go.crm.widget.button',
        }
    },
    "go.home.widget": {
        widgetType: 'TextWidget',
        widgetData: {
            path: '/home',
        },
        widgetLabels: {
            title: 'go.home.widget.title',
            text: 'go.home.widget.text',
            button: 'go.home.widget.button',
        }
    },
    "go.service.widget": {
        widgetType: 'TextWidget',
        widgetData: {
            path: '/service',
        },
        widgetLabels: {
            title: 'go.service.widget.title',
            text: 'go.service.widget.text',
            button: 'go.service.widget.button',
        }
    },
    // Stock on hand today, valued at its unit cost, by the product's item
    // group. A level rather than a total, so no period to choose: it is fixed
    // at "this month", whose end is today and whose previous period ends on the
    // same day a month ago — the KPI's comparison. Stock at a date is every
    // movement up to it (inventoryLedger/valueByGroup).
    "inventory.analytics.value.group.widget": {
        widgetType: 'DonutWidget',
        widgetData: {
            period: 'thisMonth',
            periodSelector: false,
            compare: 'previous',
            measures: {
                value: {
                    format: 'currency',
                    group: 'productgroup',
                    sources: [
                        { id: 'inventoryLedger', entity: 'inventoryLedger', aggregation: 'valueByGroup', value: 'value' },
                    ]
                },
            },
            breakdown: { value: 'value', limit: 5, emptyLabel: 'inventory.analytics.value.noGroup' },
            center: { label: 'inventory.analytics.value.center' },
            showValues: true,
            kpis: [
                { id: 'value', label: 'inventory.analytics.value.kpi', value: 'value', favorable: 'neutral' },
            ],
            path: '/inventory/productGroup',
        },
        widgetLabels: {
            title: 'inventory.analytics.value.group.title',
            subtitle: 'inventory.analytics.value.subtitle',
        }
    },
    // The same, by item — the products holding the most stock value
    "inventory.analytics.value.item.widget": {
        widgetType: 'DonutWidget',
        widgetData: {
            period: 'thisMonth',
            periodSelector: false,
            compare: 'previous',
            measures: {
                value: {
                    format: 'currency',
                    group: 'itemid',
                    name: 'itemname',
                    sources: [
                        { id: 'inventoryLedger', entity: 'inventoryLedger', aggregation: 'valueByItem', value: 'value' },
                    ]
                },
            },
            breakdown: { value: 'value', limit: 5 },
            center: { label: 'inventory.analytics.value.center' },
            showValues: true,
            kpis: [
                { id: 'value', label: 'inventory.analytics.value.kpi', value: 'value', favorable: 'neutral' },
            ],
            path: '/inventory/product',
        },
        widgetLabels: {
            title: 'inventory.analytics.value.item.title',
            subtitle: 'inventory.analytics.value.subtitle',
        }
    },
    "inventory.info.card.widget": {
        widgetType: 'InfoCardWidget',
        widgetData: {
            background: 'secondary',
            text: { position: 'left' },
            image: { name: 'products', layout: 'side', position: 'right', align: 'center', offset: { x: 0, y: 0 }, dimensions: { width: 220, height: 150 } },
            buttons: {
                position: 'left',
                items: [
                    { id: 'groups',   label: 'inventory.info.card.widget.button.groups',   path: '/inventory/productGroup',  variant: 'primary' },
                    { id: 'families', label: 'inventory.info.card.widget.button.families', path: '/inventory/productFamily', variant: 'secondary' },
                ]
            },
        },
        widgetLabels: {
            title: 'inventory.info.card.widget.title',
            description: 'inventory.info.card.widget.description',
        }
    },
    "inventory.stock.table.widget": {
        widgetType: 'TableWidget',
        widgetData: {
            statsKey: 'inventoryTotal',
            period: null,
            linkPath: '/inventory/product',
            columns: [
                { field: 'itemName', label: 'inventory.stock.table.col.name' },
                { field: 'delta',    label: 'inventory.stock.table.col.stock', format: 'number' },
            ],
            sort: { field: 'delta', direction: 'desc' },
            limit: 10,
        },
        widgetLabels: {
            title: 'inventory.stock.table.widget.title',
            emptyDescription: 'inventory.stock.table.widget.empty',
        }
    },
    // Drafts only. A posted journal is the book — it is read a month at a time
    // on the list, and never needs doing anything about; a draft is work someone
    // left unfinished, which is the only thing a dashboard is for. `path` carries
    // the tab so "show all" lands on the same set the widget was showing.
    "journal.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'journal',
            path: '/accounting/journal?tab=draft',
            rowPath: '/accounting/journal',
            serverFilter: 'state:draft',
            columns: [
                { field: 'description',   label: 'journal.list.table.description' },
                { field: 'accountingDate', label: 'journal.list.table.date', format: 'date', width: '0.6fr' },
                { field: 'totalDebitBase', label: 'journal.list.table.total', format: 'amount' },
            ],
        },
        widgetLabels: {
            title: 'journal.list.widget.title',
            emptyDescription: 'journal.list.widget.empty.description',
            emptyAction: 'journal.list.widget.empty.action',
        }
    },
    "machine.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'machine',
            path: '/manufacturing/machine',
            columns: [
                { field: 'name',   label: 'manufacturing.machine.list.table.name' },
                { field: 'status', label: 'manufacturing.machine.list.table.status' },
            ],
        },
        widgetLabels: {
            title: 'machine.list.widget.title',
            emptyDescription: 'machine.list.widget.empty.description',
            emptyAction: 'machine.list.widget.empty.action',
        }
    },
    // The work orders planned to start in the period (startTime), by where
    // they stand. Colours are fixed per state, so "completed" is green
    // whichever period is shown.
    "manufacturing.analytics.work.orders.widget": {
        widgetType: 'DonutWidget',
        widgetData: {
            period: 'last3Months',
            compare: 'none',
            measures: {
                orders: {
                    format: 'integer',
                    group: 'state',
                    sources: [
                        { id: 'workOrder', entity: 'workOrder', aggregation: 'byState', value: 'count' },
                    ]
                },
            },
            breakdown: {
                value: 'orders',
                limit: 6,
                labels: {
                    draft:      'manufacturing.analytics.state.draft',
                    released:   'manufacturing.analytics.state.released',
                    inProgress: 'manufacturing.analytics.state.inProgress',
                    completed:  'manufacturing.analytics.state.completed',
                    closed:     'manufacturing.analytics.state.closed',
                },
            },
            colors: { draft: 'neutral', released: 'info', inProgress: 'warning', completed: 'success', closed: 'secondary' },
            center: { label: 'manufacturing.analytics.workOrders.center' },
            showValues: true,
        },
        widgetLabels: {
            title: 'manufacturing.analytics.workOrders.title',
        }
    },
    "payment.account.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'paymentAccount',
            path: '/bank/account',
            columns: [
                { field: 'accountName', label: 'bank.account.list.table.name' },
                { field: 'currency',    label: 'bank.account.list.table.currency', width: '0.6fr' },
                { field: 'balance',     label: 'bank.account.list.table.balance',  format: 'amount' },
            ],
        },
        widgetLabels: {
            title: 'bank.account.list.widget.title',
            emptyDescription: 'bank.account.list.widget.empty.description',
            emptyAction: 'bank.account.list.widget.empty.action',
        }
    },
    "product.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'product',
            path: '/inventory/product',
            columns: [
                { field: 'name',        label: 'product.list.table.name' },
                { field: 'description', label: 'product.list.table.description' },
                { field: 'netSalesPrice', label: 'product.list.table.price', format: 'amount' },
            ],
        },
        widgetLabels: {
            title: 'product.list.widget.title',
            emptyDescription: 'product.list.widget.empty.description',
            emptyAction: 'product.list.widget.empty.action',
        }
    },
    "project.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'project',
            path: '/project/project',
            columns: [
                { field: 'name',   label: 'project.list.table.name' },
                { field: 'status', label: 'project.list.table.status' },
            ],
        },
        widgetLabels: {
            title: 'project.list.widget.title',
            emptyDescription: 'project.list.widget.empty.description',
            emptyAction: 'project.list.widget.empty.action',
        }
    },
    "project.task.sublist.widget": {
        widgetType: 'SubListWidget',
        widgetData: {
            path: '/project/project',
            header: [
                {
                    type: 'connect',
                    entity: 'project',
                    idField: 'projectId',
                    nameField: 'projectName',
                    menuItemField: 'name',
                    placeholder: 'project.task.list.widget.select.placeholder',
                    modalTitle: 'project.task.list.widget.title',
                    valueMap: { projectId: 'id', projectName: 'name' },
                    options: { withHeaders: false },
                    entityParams: { orderBy: 'name', direction: 'asc', serverFilter: 'archived:false' },
                    compactOnMobile: true,
                },
                {
                    type: 'filter',
                    field: 'status',
                    defaultValue: '!done',
                    compactOnMobile: true,
                    modalTitle: 'project.task.filter.title',
                    options: [
                        { label: 'project.task.filter.all', value: null },
                        { label: 'project.task.filter.open', value: '!done' },
                        { label: 'project.task.filter.done', value: 'done' },
                    ],
                },
            ],
            subEntity: 'projectTask',
            lines: [
                { field: 'title', type: 'link', weight: 2 },
                { field: 'statusName', type: 'value', weight: 1 },
            ],
        },
        widgetLabels: {
            title: 'project.task.list.widget.title',
            emptyDescription: 'project.task.list.widget.empty.description',
            emptyAction: 'project.task.list.widget.empty.action',
        }
    },
    // Tasks finished in the period, across every project
    "project.totals.widget": {
        widgetType: 'KpiTilesWidget',
        widgetData: {
            period: 'thisMonth',
            compare: 'previous',
            measures: {
                doneTasks: {
                    format: 'integer',
                    sources: [
                        { id: 'projectTask', entity: 'project/all/projectTask', aggregation: 'doneByPeriod', params: { status: 'done' }, value: 'count' },
                    ]
                },
            },
            tiles: [
                { id: 'doneTasks', label: 'project.totals.doneTasks.title', value: 'doneTasks', favorable: 'up', path: '/project/project' },
            ]
        },
        widgetLabels: { title: 'project.totals.widget.title' }
    },
    "purchase.order.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'purchaseOrder',
            path: '/purchases/purchaseOrder',
            columns: [
                { field: 'number',                       label: 'purchases.purchaseOrder.list.table.number',      width: '0.8fr' },
                { field: 'supplierName',                 label: 'purchases.purchaseOrder.list.table.supplier' },
                { field: 'totalNetAmountBaseCurrency',   label: 'purchases.purchaseOrder.list.table.totalAmount', format: 'amount', width: '0.9fr' },
            ],
        },
        widgetLabels: {
            title: 'purchase.order.list.widget.title',
            emptyDescription: 'purchase.order.list.widget.empty.description',
            emptyAction: 'purchase.order.list.widget.empty.action',
        }
    },
    // Purchase orders booked per bucket, the current one in full colour.
    "purchases.analytics.orders.placed.widget": {
        widgetType: 'VerticalBarWidget',
        widgetData: {
            period: 'last6Months',
            compare: 'previous',
            measures: {
                orders: {
                    format: 'integer',
                    sources: [
                        { id: 'purchaseOrder', entity: 'purchaseOrder', aggregation: 'byPeriod', params: { state: 'final' }, value: 'count' },
                    ]
                },
                value: {
                    format: 'currency',
                    sources: [
                        { id: 'purchaseOrder', entity: 'purchaseOrder', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                    ]
                },
            },
            headline: { id: 'orders', label: 'purchases.analytics.orders.headline', value: 'orders', favorable: 'neutral' },
            series: [
                { id: 'orders', label: 'purchases.analytics.orders.series', value: 'orders', color: 'primary' },
            ],
            highlight: 'last',
            valueLabels: true,
            kpis: [
                { id: 'value',   label: 'purchases.analytics.orders.value',   value: 'value', favorable: 'neutral' },
                { id: 'average', label: 'purchases.analytics.orders.average', value: { divide: ['value', 'orders'] }, format: 'currency', favorable: 'neutral' },
                { id: 'peak',    label: 'purchases.analytics.orders.peak',    value: { max: 'orders' }, format: 'integer', change: 'none' },
            ]
        },
        widgetLabels: {
            title: 'purchases.analytics.orders.title',
        }
    },
    // Where the money goes: booked expenses by supplier, the five largest and
    // the rest as one.
    "purchases.analytics.spend.supplier.widget": {
        widgetType: 'DonutWidget',
        widgetData: {
            period: 'last12Months',
            compare: 'none',
            measures: {
                spend: {
                    format: 'currency',
                    group: 'supplierid',
                    name: 'suppliername',
                    sources: [
                        { id: 'expense', entity: 'expense', aggregation: 'bySupplier', params: { state: 'final' }, value: 'net' },
                    ]
                },
            },
            breakdown: { value: 'spend', limit: 5, emptyLabel: 'purchases.analytics.spend.noSupplier' },
            center: { label: 'purchases.analytics.spend.center' },
            path: '/purchases/expense',
        },
        widgetLabels: {
            title: 'purchases.analytics.spend.title',
        }
    },
    // Booked expenses' net amount per category, the largest first and the rest
    // as one. Read live off the expenses' posting groups (expense/byCategory),
    // so any period works; an expense without a category is its own row.
    "purchases.expense.category.widget": {
        widgetType: 'HorizontalBarWidget',
        widgetData: {
            period: 'last12Months',
            compare: 'previous',
            measures: {
                spend: {
                    format: 'currency',
                    group: 'expenseaccountid',
                    name: 'expenseaccountidname',
                    sources: [
                        { id: 'expense', entity: 'expense', aggregation: 'byCategory', params: { state: 'final' }, value: 'net' },
                    ]
                },
            },
            breakdown: { value: 'spend', limit: 8, other: true, emptyLabel: 'purchases.expense.category.widget.uncategorised' },
            display: 'both',
            kpis: [
                { id: 'spend', label: 'purchases.expense.category.widget.total', value: 'spend', favorable: 'down' },
            ],
            path: '/purchases/expense',
        },
        widgetLabels: {
            title: 'purchases.expense.category.widget.title',
        }
    },
    // What was ordered from suppliers in the period, and in how many orders
    "purchases.totals.widget": {
        widgetType: 'KpiTilesWidget',
        widgetData: {
            period: 'thisMonth',
            compare: 'previous',
            measures: {
                purchased: {
                    format: 'currency',
                    sources: [
                        { id: 'purchaseOrder', entity: 'purchaseOrder', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                    ]
                },
                orders: {
                    format: 'integer',
                    sources: [
                        { id: 'purchaseOrder', entity: 'purchaseOrder', aggregation: 'byPeriod', params: { state: 'final' }, value: 'count' },
                    ]
                },
            },
            tiles: [
                { id: 'purchased', label: 'purchases.totals.purchased.title', value: 'purchased', favorable: 'neutral', path: '/purchases/purchaseOrder' },
                { id: 'orders',    label: 'purchases.totals.orders.title',    value: 'orders',    favorable: 'neutral', path: '/purchases/purchaseOrder' },
            ]
        },
        widgetLabels: { title: 'purchases.totals.widget.title' }
    },
    // What happened in sales lately, newest first, across four lists.
    "sales.analytics.activity.widget": {
        widgetType: 'ActivityFeedWidget',
        widgetData: {
            period: 'last30Days',
            periods: ['today', 'yesterday', 'last7Days', 'last30Days', 'thisMonth', 'lastMonth', 'custom'],
            feed: {
                limit: 10,
                groupByDay: true,
                filters: true,
                sources: [
                    {
                        id: 'invoice', entity: 'invoice', filter: 'state:final', dateField: 'date',
                        icon: 'invoice', color: 'primary',
                        title: 'sales.analytics.feed.invoice', chip: 'sales.analytics.feed.invoice.chip',
                        subject: ['customerName'], reference: 'number',
                        amount: { field: 'totalNetSalesAmountBaseCurrency', format: 'currency' },
                        path: '/sales/invoice',
                    },
                    {
                        id: 'payment', entity: 'invoicePayment', dateField: 'paymentDate',
                        icon: 'money', color: 'success',
                        title: 'sales.analytics.feed.payment', chip: 'sales.analytics.feed.payment.chip',
                        subject: ['customerName'], reference: 'invoiceNumber',
                        amount: { field: 'paymentAmountBaseCurrency', format: 'currency' },
                    },
                    {
                        id: 'quote', entity: 'quote', filter: 'state:final', dateField: 'date',
                        icon: 'quote', color: 'secondary',
                        title: 'sales.analytics.feed.quote', chip: 'sales.analytics.feed.quote.chip',
                        subject: ['customerName'], reference: 'number',
                        amount: { field: 'totalNetSalesAmountBaseCurrency', format: 'currency' },
                        path: '/sales/quote',
                    },
                    {
                        id: 'creditNote', entity: 'creditNote', filter: 'state:final', dateField: 'date',
                        icon: 'creditNote', color: 'warning',
                        title: 'sales.analytics.feed.creditNote', chip: 'sales.analytics.feed.creditNote.chip',
                        subject: ['customerName'], reference: 'number',
                        amount: { field: 'totalNetSalesAmountBaseCurrency', format: 'currency' },
                        path: '/sales/creditNote',
                    },
                ],
            },
        },
        widgetLabels: {
            title: 'sales.analytics.activity.title',
        }
    },
    // Net sales (invoices less credit notes) over the period, with the period
    // before drawn underneath for comparison.
    "sales.analytics.net.sales.widget": {
        widgetType: 'AreaChartWidget',
        widgetData: {
            period: 'last30Days',
            compare: 'previous',
            measures: {
                sales: {
                    format: 'currency',
                    sources: [
                        { id: 'invoice',    entity: 'invoice',    aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                        { id: 'creditNote', entity: 'creditNote', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net', sign: -1 },
                    ]
                },
                invoices: {
                    format: 'integer',
                    sources: [
                        { id: 'invoice', entity: 'invoice', aggregation: 'byPeriod', params: { state: 'final' }, value: 'count' },
                    ]
                },
                credited: {
                    format: 'currency',
                    sources: [
                        { id: 'creditNote', entity: 'creditNote', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                    ]
                },
            },
            headline: { id: 'sales', label: 'sales.analytics.netSales.headline', value: 'sales' },
            series: [
                { id: 'sales', label: 'sales.analytics.netSales.series', value: 'sales', color: 'primary' },
            ],
            showComparison: true,
            kpis: [
                { id: 'invoices', label: 'sales.analytics.netSales.invoices', value: 'invoices' },
                { id: 'average',  label: 'sales.analytics.netSales.average',  value: { divide: ['sales', 'invoices'] }, format: 'currency' },
                { id: 'credited', label: 'sales.analytics.netSales.credited', value: 'credited', favorable: 'down' },
            ]
        },
        widgetLabels: {
            title: 'sales.analytics.netSales.title',
        }
    },
    // How much of what was invoiced in the period has been paid, by amount.
    "sales.analytics.paid.share.widget": {
        widgetType: 'GaugeWidget',
        widgetData: {
            period: 'last3Months',
            compare: 'previous',
            measures: {
                invoiced: {
                    format: 'currency',
                    sources: [
                        { id: 'invoice', entity: 'invoice', aggregation: 'byPaid', params: { state: 'final' }, value: 'net' },
                    ]
                },
                paid: {
                    format: 'currency',
                    sources: [
                        { id: 'invoice', entity: 'invoice', aggregation: 'byPaid', params: { state: 'final' }, value: 'net', match: { paid: 'true' } },
                    ]
                },
                unpaidCount: {
                    format: 'integer',
                    sources: [
                        { id: 'invoice', entity: 'invoice', aggregation: 'byPaid', params: { state: 'final' }, value: 'count', match: { paid: 'false' } },
                    ]
                },
            },
            gauge: {
                value: { percentOf: ['paid', 'invoiced'] },
                format: 'percent',
                min: 0,
                max: 100,
                bands: [
                    { id: 'low',  to: 50,  color: 'danger',  label: 'sales.analytics.paidShare.band.low' },
                    { id: 'fair', to: 80,  color: 'warning', label: 'sales.analytics.paidShare.band.fair' },
                    { id: 'good', to: 100, color: 'success', label: 'sales.analytics.paidShare.band.good' },
                ],
                target: 90,
                favorable: 'up',
            },
            kpis: [
                { id: 'paid',        label: 'sales.analytics.paidShare.paid',        value: 'paid' },
                { id: 'outstanding', label: 'sales.analytics.paidShare.outstanding', value: { subtract: ['invoiced', 'paid'] }, format: 'currency', favorable: 'down' },
                { id: 'unpaid',      label: 'sales.analytics.paidShare.unpaid',      value: 'unpaidCount', favorable: 'down' },
            ]
        },
        widgetLabels: {
            title: 'sales.analytics.paidShare.title',
            subtitle: 'sales.analytics.paidShare.subtitle',
        }
    },
    // The newest booked invoices of the period, with where each stands.
    "sales.analytics.recent.invoices.widget": {
        widgetType: 'DataTableWidget',
        widgetData: {
            period: 'last30Days',
            compare: 'none',
            table: {
                entity: 'invoice',
                filter: 'state:final',
                dateField: 'date',
                limit: 8,
                rowPath: '/sales/invoice',
                columns: [
                    { id: 'number',   label: 'sales.analytics.invoices.number',   field: 'number' },
                    { id: 'customer', label: 'sales.analytics.invoices.customer', field: 'customerName', hideBelow: 330 },
                    { id: 'amount',   label: 'sales.analytics.invoices.amount',   field: 'totalNetSalesAmountBaseCurrency', format: 'currency' },
                    { id: 'date',     label: 'sales.analytics.invoices.date',     field: 'date', format: 'relative', hideBelow: 520 },
                    {
                        id: 'status', label: 'sales.analytics.invoices.status', field: 'paid', format: 'status', hideBelow: 420,
                        statuses: [
                            { id: 'paid',    condition: { field: 'paid', operator: 'equals', value: true }, label: 'sales.analytics.status.paid', tone: 'success' },
                            { id: 'overdue', pastDue: 'paymentDate', label: 'sales.analytics.status.overdue', tone: 'error' },
                            { id: 'open',    label: 'sales.analytics.status.open', tone: 'info' },
                        ]
                    },
                ],
            },
            path: '/sales/invoice',
        },
        widgetLabels: {
            title: 'sales.analytics.invoices.title',
        }
    },
    // The customers who bought the most, as a ranking with their share.
    "sales.analytics.top.customers.widget": {
        widgetType: 'HorizontalBarWidget',
        widgetData: {
            period: 'last12Months',
            compare: 'none',
            measures: {
                sales: {
                    format: 'currency',
                    group: 'customerid',
                    name: 'customername',
                    sources: [
                        { id: 'invoice',    entity: 'invoice',    aggregation: 'byCustomer', params: { state: 'final' }, value: 'net' },
                        { id: 'creditNote', entity: 'creditNote', aggregation: 'byCustomer', params: { state: 'final' }, value: 'net', sign: -1 },
                    ]
                },
            },
            breakdown: { value: 'sales', limit: 6, other: true },
            display: 'both',
            ranks: true,
            colors: true,
            rowPath: '/sales/contact',
        },
        widgetLabels: {
            title: 'sales.analytics.topCustomers.title',
        }
    },
    "sales.info.card.widget": {
        widgetType: 'InfoCardWidget',
        widgetData: {
            background: 'primary',
            text: { position: 'left' },
            image: { name: 'paperPlane', layout: 'inline', position: 'right', align: 'center', offset: { x: 0, y: 0 }, dimensions: { width: 200, height: 150 } },
            buttons: {
                position: 'left',
                items: [
                    { id: 'create', label: 'sales.info.card.widget.button.create', path: '/sales/invoice/new', variant: 'primary' },
                    { id: 'list',   label: 'sales.info.card.widget.button.list',   path: '/sales/invoice',     variant: 'secondary' },
                ]
            },
        },
        widgetLabels: {
            title: 'sales.info.card.widget.title',
            description: 'sales.info.card.widget.description',
        }
    },
    // Booked invoices, net of VAT, per bucket — what the invoicedMonthly stat
    // gave, for any period. Still keyed "monthly": the dashboards name it so.
    "sales.invoiced.monthly.widget": {
        widgetType: 'VerticalBarWidget',
        widgetData: {
            period: 'last12Months',
            compare: 'previous',
            measures: {
                invoiced: {
                    format: 'currency',
                    sources: [
                        { id: 'invoice', entity: 'invoice', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                    ]
                },
                invoices: {
                    format: 'integer',
                    sources: [
                        { id: 'invoice', entity: 'invoice', aggregation: 'byPeriod', params: { state: 'final' }, value: 'count' },
                    ]
                },
            },
            headline: { id: 'invoiced', label: 'widget.sales.monthly.headline', value: 'invoiced', favorable: 'up' },
            series: [
                { id: 'invoiced', label: 'widget.sales.monthly.series', value: 'invoiced', color: 'primary' },
            ],
            highlight: 'last',
            valueLabels: true,
            kpis: [
                { id: 'invoices', label: 'widget.sales.monthly.invoices', value: 'invoices', favorable: 'up' },
                { id: 'average',  label: 'widget.sales.monthly.average',  value: { divide: ['invoiced', 'invoices'] }, format: 'currency', favorable: 'up' },
                { id: 'peak',     label: 'widget.sales.monthly.peak',     value: { max: 'invoiced' }, format: 'currency', change: 'none' },
            ],
            path: '/sales/invoice'
        },
        widgetLabels: {
            title: 'widget.sales.monthly.title',
            subtitle: 'widget.sales.monthly.text'
        }
    },
    // Invoiced less credited, what was credited, and how much of the invoicing
    // that was. The year figure the stats widget showed is the period selector's
    // "Year to date" now.
    "sales.net.sales.widget": {
        widgetType: 'KpiTilesWidget',
        widgetData: {
            period: 'thisMonth',
            compare: 'previous',
            measures: {
                netSales: {
                    format: 'currency',
                    sources: [
                        { id: 'invoice',    entity: 'invoice',    aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                        { id: 'creditNote', entity: 'creditNote', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net', sign: -1 },
                    ]
                },
                invoiced: {
                    format: 'currency',
                    sources: [
                        { id: 'invoice', entity: 'invoice', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                    ]
                },
                credited: {
                    format: 'currency',
                    sources: [
                        { id: 'creditNote', entity: 'creditNote', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                    ]
                },
            },
            tiles: [
                { id: 'netSales',    label: 'sales.net.sales.netSales.title',    value: 'netSales', favorable: 'up',   path: '/sales/invoice' },
                { id: 'credited',    label: 'sales.net.sales.creditNotes.title', value: 'credited', favorable: 'down', path: '/sales/creditNote' },
                { id: 'creditShare', label: 'sales.net.sales.creditShare.title', value: { percentOf: ['credited', 'invoiced'] }, favorable: 'down' },
            ]
        },
        widgetLabels: { title: 'sales.net.sales.widget.title' }
    },
    // The month's — or any period's — sales activity at a glance
    "sales.totals.widget": {
        widgetType: 'KpiTilesWidget',
        widgetData: {
            period: 'thisMonth',
            compare: 'previous',
            measures: {
                invoiced: {
                    format: 'currency',
                    sources: [
                        { id: 'invoice', entity: 'invoice', aggregation: 'byPeriod', params: { state: 'final' }, value: 'net' },
                    ]
                },
                invoices: {
                    format: 'integer',
                    sources: [
                        { id: 'invoice', entity: 'invoice', aggregation: 'byPeriod', params: { state: 'final' }, value: 'count' },
                    ]
                },
                quotes: {
                    format: 'integer',
                    sources: [
                        { id: 'quote', entity: 'quote', aggregation: 'byPeriod', params: { state: 'final' }, value: 'count' },
                    ]
                },
                shipments: {
                    format: 'integer',
                    sources: [
                        { id: 'stockShipment', entity: 'stockShipment', aggregation: 'byPeriod', params: { state: 'final' }, value: 'count' },
                    ]
                },
            },
            tiles: [
                { id: 'invoiced',  label: 'sales.totals.invoiced.title',     value: 'invoiced',  favorable: 'up', path: '/sales/invoice' },
                { id: 'invoices',  label: 'sales.totals.invoiceCount.title', value: 'invoices',  favorable: 'up', path: '/sales/invoice' },
                { id: 'quotes',    label: 'sales.totals.quotes.title',       value: 'quotes',    favorable: 'up', path: '/sales/quote' },
                { id: 'shipments', label: 'sales.totals.shipments.title',    value: 'shipments', favorable: 'up', path: '/inventory/shipment' },
            ]
        },
        widgetLabels: { title: 'sales.totals.widget.title' }
    },
    // Tickets raised per bucket, split by priority, low at the bottom.
    "service.analytics.tickets.priority.widget": {
        widgetType: 'StackedBarWidget',
        widgetData: {
            period: 'last3Months',
            compare: 'previous',
            measures: {
                tickets: {
                    format: 'integer',
                    group: 'priority',
                    sources: [
                        { id: 'serviceTicket', entity: 'serviceTicket', aggregation: 'byPeriodAndPriority', value: 'count' },
                    ]
                },
                urgent: {
                    format: 'integer',
                    sources: [
                        { id: 'high',     entity: 'serviceTicket', aggregation: 'byPeriodAndPriority', value: 'count', match: { priority: 'high' } },
                        { id: 'critical', entity: 'serviceTicket', aggregation: 'byPeriodAndPriority', value: 'count', match: { priority: 'critical' } },
                    ]
                },
                critical: {
                    format: 'integer',
                    sources: [
                        { id: 'critical', entity: 'serviceTicket', aggregation: 'byPeriodAndPriority', value: 'count', match: { priority: 'critical' } },
                    ]
                },
            },
            stack: {
                value: 'tickets',
                otherLabel: 'service.analytics.priority.none',
                segments: [
                    { id: 'low',      label: 'service.analytics.priority.low',      color: 'info' },
                    { id: 'medium',   label: 'service.analytics.priority.medium',   color: 'success' },
                    { id: 'high',     label: 'service.analytics.priority.high',     color: 'warning' },
                    { id: 'critical', label: 'service.analytics.priority.critical', color: 'danger' },
                ],
            },
            totals: true,
            kpis: [
                { id: 'tickets',  label: 'service.analytics.tickets.total',    value: 'tickets',  favorable: 'neutral' },
                { id: 'urgent',   label: 'service.analytics.tickets.urgent',   value: 'urgent',   favorable: 'down' },
                { id: 'critical', label: 'service.analytics.tickets.critical', value: 'critical', favorable: 'down' },
            ]
        },
        widgetLabels: {
            title: 'service.analytics.tickets.title',
        }
    },
    "service.asset.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'asset',
            path: '/service/asset',
            columns: [
                { field: 'name',         label: 'service.asset.list.table.name' },
                { field: 'customerName', label: 'service.asset.list.table.customer' },
                { field: 'status',       label: 'service.asset.list.table.status' }
            ]
        },
        widgetLabels: {
            title: 'service.asset.widget.title',
            emptyDescription: 'service.asset.widget.empty',
            emptyAction: 'service.asset.widget.action'
        }
    },
    "service.create.product.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'product',
            path: '/crm/product',
            columns: [
                { field: 'name',        label: 'product.list.table.name' },
                { field: 'description', label: 'product.list.table.description' },
                { field: 'netSalesPrice', label: 'product.list.table.price', format: 'amount' },
            ],
        },
        widgetLabels: {
            title: 'product.list.widget.title',
            emptyDescription: 'product.list.widget.empty.description',
            emptyAction: 'product.list.widget.empty.action',
        }
    },
    "service.text.widget": {
        widgetType: 'TextWidget',
        widgetData: {},
        widgetLabels: {
            title: 'service.text.widget.title',
            text: 'service.text.widget.text',
            button: 'service.text.widget.button',
            path: '/service/serviceTicket'
        }
    },
    "service.ticket.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'serviceTicket',
            path: '/service/serviceTicket',
            columns: [
                { field: 'title',        label: 'service.serviceTicket.list.table.title' },
                { field: 'customerName', label: 'service.serviceTicket.list.table.customer' },
                { field: 'status',       label: 'service.serviceTicket.list.table.status' }
            ]
        },
        widgetLabels: {
            title: 'service.ticket.widget.title',
            emptyDescription: 'service.ticket.widget.empty',
            emptyAction: 'service.ticket.widget.action'
        }
    },
    "service.workOrder.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'serviceWorkOrder',
            path: '/service/serviceWorkOrder',
            columns: [
                { field: 'customerName',  label: 'service.serviceWorkOrder.list.table.customer' },
                { field: 'technicianName', label: 'service.serviceWorkOrder.list.table.technician' },
                { field: 'status',         label: 'service.serviceWorkOrder.list.table.status' }
            ]
        },
        widgetLabels: {
            title: 'service.workOrder.widget.title',
            emptyDescription: 'service.workOrder.widget.empty',
            emptyAction: 'service.workOrder.widget.action'
        }
    },
    "warehouse.pick.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            compact: true,
            entity: 'warehouseOrder',
            path: '/warehouse/warehouseOrder/released?tab=pick',
            rowPath: '/warehouse/warehouseOrder/pick',
            serverFilter: 'type:PICK;state:released;completed:false',
            columns: [
                { field: 'number',         label: 'warehouseOrder.list.table.number' },
                { field: 'state',          label: 'warehouseOrder.list.table.status' },
                { field: 'assignedToName', label: 'warehouseOrder.list.table.assignedTo' },
            ],
        },
        widgetLabels: {
            title: 'warehouse.widget.pick.title',
            emptyDescription: 'warehouse.widget.pick.empty',
            emptyAction: 'warehouse.widget.pick.action',
        }
    },
    "warehouse.putaway.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            compact: true,
            entity: 'warehouseOrder',
            path: '/warehouse/warehouseOrder/released?tab=putaway',
            rowPath: '/warehouse/warehouseOrder/putaway',
            serverFilter: 'type:PUTAWAY;state:released;completed:false',
            columns: [
                { field: 'number',       label: 'warehouseOrder.list.table.number' },
                { field: 'state',        label: 'warehouseOrder.list.table.status' },
                { field: 'warehouseName',label: 'warehouseOrder.list.table.warehouse' },
            ],
        },
        widgetLabels: {
            title: 'warehouse.widget.putaway.title',
            emptyDescription: 'warehouse.widget.putaway.empty',
            emptyAction: 'warehouse.widget.putaway.action',
        }
    },
    "warehouse.quarantine.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            compact: true,
            entity: 'warehouseOrder',
            path: '/warehouse/warehouseOrder/released?tab=quarantine',
            rowPath: '/warehouse/warehouseOrder/quarantine',
            serverFilter: 'type:QUARANTINE;state:released;completed:false',
            columns: [
                { field: 'number',        label: 'warehouseOrder.list.table.number' },
                { field: 'state',         label: 'warehouseOrder.list.table.status' },
                { field: 'warehouseName', label: 'warehouseOrder.list.table.warehouse' },
            ],
        },
        widgetLabels: {
            title: 'warehouse.widget.quarantine.title',
            emptyDescription: 'warehouse.widget.quarantine.empty',
            emptyAction: 'warehouse.widget.quarantine.action',
        }
    },
    "warehouse.return.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            compact: true,
            entity: 'warehouseOrder',
            path: '/warehouse/warehouseOrder/released?tab=return',
            rowPath: '/warehouse/warehouseOrder/return',
            serverFilter: 'type:RETURN;state:released;completed:false',
            columns: [
                { field: 'number',        label: 'warehouseOrder.list.table.number' },
                { field: 'state',         label: 'warehouseOrder.list.table.status' },
                { field: 'warehouseName', label: 'warehouseOrder.list.table.warehouse' },
            ],
        },
        widgetLabels: {
            title: 'warehouse.widget.return.title',
            emptyDescription: 'warehouse.widget.return.empty',
            emptyAction: 'warehouse.widget.return.action',
        }
    },
    "warehouse.transfer.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            compact: true,
            entity: 'warehouseOrder',
            path: '/warehouse/warehouseOrder/released?tab=transfer',
            rowPath: '/warehouse/warehouseOrder/transfer',
            serverFilter: 'type:TRANSFER;state:released;completed:false',
            columns: [
                { field: 'number',         label: 'warehouseOrder.list.table.number' },
                { field: 'state',          label: 'warehouseOrder.list.table.status' },
                { field: 'assignedToName', label: 'warehouseOrder.list.table.assignedTo' },
            ],
        },
        widgetLabels: {
            title: 'warehouse.widget.transfer.title',
            emptyDescription: 'warehouse.widget.transfer.empty',
            emptyAction: 'warehouse.widget.transfer.action',
        }
    },
    "work.order.list.widget": {
        widgetType: 'ListWidget',
        widgetData: {
            entity: 'workOrder',
            path: '/manufacturing/workOrder',
            columns: [
                { field: 'number', label: 'manufacturing.workOrder.list.table.number' },
                { field: 'state',  label: 'manufacturing.workOrder.list.table.state' },
            ],
        },
        widgetLabels: {
            title: 'work.order.list.widget.title',
            emptyDescription: 'work.order.list.widget.empty.description',
            emptyAction: 'work.order.list.widget.empty.action',
        }
    },
}